import { SelectedMenuProductList, SelectedMenu, SelectedMenuContent } from 'rangeConfig/@types';
import { Connectors, CoreApi, ProductType } from '@inter-ikea-storageone/range';
import deleteProduct from 'rangeConfig/shared/behaviors/deleteProduct';
import autoFillHandlesBehavior from 'rangeConfig/shared/behaviors/autoFillHandles';
import replaceSlidingDoorPanel from 'rangeConfig/pax/behaviors/replaceSlidingDoorPanel';
import replaceSlidingDoorPanelFrame from 'rangeConfig/pax/behaviors/replaceSlidingDoorPanelFrame';
import ChangePanelLayout from 'rangeConfig/pax/widgets/panelCustomisation/ChangePanelLayout';
import hasPanelArrangementSolid from 'rangeConfig/pax/widgets/panelCustomisation/utils/hasPanelArrangementSolid';
import getPanelSelectedIds from 'rangeConfig/pax/widgets/panelCustomisation/utils/getPanelSelectedIds';
import PanelSelection from 'rangeConfig/pax/widgets/panelCustomisation/types/PanelSelection';
import removeHandleFromSelectedEntity from 'rangeConfig/shared/behaviors/removeHandleFromSelectedEntity';
import { colorFilter, handlesTypeFilter } from 'rangeConfig/pax/filters/configFilters';
import platform from 'rangeConfig/shared/utils/platform';
import { noProductsWithTypesForSelectedEntity } from 'rangeConfig/shared/rules/noneOptionSelectedRules';
import {
  matchHandleSizeToFront,
  matchHandleTypeToFront,
  matchHeight,
  matchWidth
} from 'rangeConfig/shared/filters/selectedMenuProductFilters';
import {
  canHaveHandles,
  hasPanels,
  matchCustomSlidingDoor,
  matchDoorToFrameType,
  matchDoorType,
  matchPanelSize
} from 'rangeConfig/pax/filters/selectedMenuProductFilters';
import { hideOpeningWidget } from 'rangeConfig/pax/rules/hideMenuRules';
import withDoorOpeningDirection from 'rangeConfig/shared/widgets/doorOpeningDirection/withDoorOpeningDirection';
import DoorOpeningDirectionWidget from 'rangeConfig/shared/widgets/doorOpeningDirection/DoorOpeningDirectionWidget';
import { hideHandlesMenuOnDoorOnCornerFrame } from 'rangeConfig/pax/rules/hideMenuRules';
import replaceDoor from 'rangeConfig/shared/behaviors/replaceDoor';
import { getPanelHoverImage, getPanelMainImage } from 'rangeConfig/pax/rules/images';
import { isSelectedEntityProductType } from 'rangeConfig/shared/rules/hideMenuRules';
import { selectedDoorNotCustomised } from 'rangeConfig/pax/utils/utils';
import showHandlesDrillTemplateSheet from 'rangeConfig/shared/behaviors/showHandlesDrillTemplateSheet';

export const doors: SelectedMenuProductList = {
  id: 'doors-selected-menu-doors',
  title: 'storageone.productListDoorsTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'designText, communicatedSizeText',
  additionalItemsText: 'storageone.selectedProductAdditionalDoors',
  behaviors: {
    productClicked: [replaceDoor],
    productDropped: [replaceDoor]
  },
  productTypes: [
    ProductType.PAX_HINGE_DOOR_ASM,
    ProductType.PAX_SLIDING_DOOR_REGULAR_ASM,
    ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM
  ],
  filters: [colorFilter],
  selected: selectedDoorNotCustomised,
  productFilters: [
    matchDoorType,
    matchWidth,
    matchHeight,
    matchCustomSlidingDoor,
    matchDoorToFrameType
  ],
  useMLSorting: true
};

const customizeSlidingDoors: SelectedMenuContent[] = [
  {
    id: 'change-panel-layout',
    title: 'storageone.slidingDoorPanelLayout',
    component: ChangePanelLayout,
    hidden: (coreApi: CoreApi) =>
      !isSelectedEntityProductType(coreApi, ProductType.PAX_SLIDING_DOOR_WITH_PANELS_ASM)
  },
  {
    id: 'doors-selected-menu-panel',
    title: 'storageone.slidingDoorPanel',
    cardsTitle: 'name',
    cardsSubtitle: 'designText',
    productCardMainImage: getPanelMainImage,
    productCardHoverImage: getPanelHoverImage,
    behaviors: {
      productClicked: [replaceSlidingDoorPanel(PanelSelection.Primary)],
      productDropped: [replaceSlidingDoorPanel(PanelSelection.Primary)]
    },
    productTypes: [ProductType.PAX_SLIDING_DOOR_PANEL],
    hidden: (coreApi: CoreApi, selectedEntityId: number) =>
      !hasPanelArrangementSolid(coreApi, selectedEntityId),
    overrideSelection: getPanelSelectedIds(PanelSelection.Primary),
    productFilters: [hasPanels, matchPanelSize]
  },
  {
    id: 'doors-selected-menu-panel-primary',
    title: 'storageone.slidingDoorPanelNumber',
    titleValue: { panelNumber: 1 },
    cardsTitle: 'name',
    cardsSubtitle: 'designText',
    productCardMainImage: getPanelMainImage,
    productCardHoverImage: getPanelHoverImage,
    behaviors: {
      productClicked: [replaceSlidingDoorPanel(PanelSelection.Primary)],
      productDropped: [replaceSlidingDoorPanel(PanelSelection.Primary)]
    },
    productTypes: [ProductType.PAX_SLIDING_DOOR_PANEL],
    hidden: hasPanelArrangementSolid,
    overrideSelection: getPanelSelectedIds(PanelSelection.Primary),
    productFilters: [hasPanels, matchPanelSize]
  },
  {
    id: 'doors-selected-menu-panel-secondary',
    title: 'storageone.slidingDoorPanelNumber',
    titleValue: { panelNumber: 2 },
    cardsTitle: 'name',
    cardsSubtitle: 'designText',
    productCardMainImage: getPanelMainImage,
    productCardHoverImage: getPanelHoverImage,
    behaviors: {
      productClicked: [replaceSlidingDoorPanel(PanelSelection.Secondary)],
      productDropped: [replaceSlidingDoorPanel(PanelSelection.Secondary)]
    },
    productTypes: [ProductType.PAX_SLIDING_DOOR_PANEL],
    hidden: hasPanelArrangementSolid,
    overrideSelection: getPanelSelectedIds(PanelSelection.Secondary),
    productFilters: [hasPanels, matchPanelSize]
  },
  {
    id: 'doors-selected-menu-panel-frames',
    title: 'storageone.productListSlidingDoorFramesTitle',
    behaviors: { productClicked: [replaceSlidingDoorPanelFrame] },
    productTypes: [ProductType.PAX_SLIDING_DOOR_PANEL_FRAME],
    productFilters: [matchWidth, matchHeight, hasPanels]
  }
];

const handles: SelectedMenuProductList = {
  id: 'doors-selected-menu-handles',
  title: 'storageone.productListHandlesTitle',
  cardsTitle: 'name',
  cardsSubtitle: 'typeName, designText, communicatedSizeText',
  additionalItemsText: 'storageone.selectedProductAdditionalHandles',
  behaviors: {
    productClicked: [autoFillHandlesBehavior],
    productDropped: [autoFillHandlesBehavior]
  },
  productTypes: [ProductType.HANDLE, ProductType.KNOB, ProductType.EDGE_HANDLE],
  filters: [handlesTypeFilter, colorFilter],
  productFilters: [canHaveHandles, matchHandleSizeToFront, matchHandleTypeToFront],
  noneOption: {
    behaviors: { removeProducts: [removeHandleFromSelectedEntity] },
    selected: noProductsWithTypesForSelectedEntity([
      ProductType.HANDLE,
      ProductType.KNOB,
      ProductType.EDGE_HANDLE
    ]),
    title: 'storageone.productListNoHandlesOptionTitle'
  },
  hidden: hideHandlesMenuOnDoorOnCornerFrame,
  useMLSorting: true
};

const widgets = {
  desktop: [
    {
      id: 'doorOpeningDirection',
      title: 'storageone.doorOpeningDirectionTitle',
      component: withDoorOpeningDirection(
        Connectors.DOOR_LEFT,
        Connectors.DOOR_RIGHT,
        Connectors.DOOR_CONNECTION_LEFT,
        Connectors.DOOR_CONNECTION_RIGHT
      )(DoorOpeningDirectionWidget),
      hidden: hideOpeningWidget
    },
    doors,
    handles,
    ...customizeSlidingDoors
  ],
  mobile: [doors, handles, ...customizeSlidingDoors]
};

const selectedMenu: SelectedMenu = {
  menuTitle: 'storageone.selectedProductListCustomiseDoor',
  behaviors: {
    productDeleted: [deleteProduct],
    selectedMenuExited: [showHandlesDrillTemplateSheet]
  },
  content: widgets[platform]
};

export default selectedMenu;
